'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { Address } from 'viem';
import { isDefined } from '@morpho-org/morpho-ts';
import { CursorPagination } from '@morpho-org/ui-v6/client';
import { useTable } from '@tanstack/react-table';
import { startTransition, useCallback, useMemo, useState } from 'react';
import { DateRangeFilter, dateRangeToTimestamps, type DateRangeValue } from '@/components/common/DateRangeFilter';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import { TransactionTypeFilter } from '@/components/common/TransactionTypeFilter';
import { VaultTransactionsLayout } from '@/components/VaultPage/components/VaultTransactionsLayout';
import { getVaultTransactionsColumns, PAGE_SIZE, VAULT_V2_TRANSACTION_FILTER_OPTIONS as TRANSACTION_FILTER_OPTIONS, VAULT_V2_TO_VAULT_TRANSACTION_TYPE, VAULT_V2_TRANSACTION_TYPE_LABELS } from '@/components/VaultPage/vaultTransactions/config';
import { useVaultTransactionsTableData } from '@/components/VaultPage/vaultTransactions/hooks/useVaultTransactionsTableData';
import { toVaultTransactionsTableRows } from '@/components/VaultPage/vaultTransactions/toVaultTransactionsTableRows';
import VaultV2TransactionsTableContent from '@/components/VaultPage/VaultV2/components/VaultV2TransactionsTableContent';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useIsBelowBreakpoint } from '@/hooks/useBreakpoints';
import { useSkeletonRows } from '@/hooks/useSkeletonRows';
import { VaultV2TransactionType } from '@/types/enums';
import { vaultTransactionsTableFeatures } from '../../../vaultTransactions/config/VaultTransactions.columns';
export interface VaultV2YourTransactionsClientProps {
  address: Address;
  chainId: ChainId;
  title: string;
}
const columnsDefs = getVaultTransactionsColumns();
const VaultV2YourTransactionsClient = t0 => {
  const $ = _c(53);
  const {
    address,
    chainId,
    title
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [transactionTypeFilter, setTransactionTypeFilter] = useState(t1);
  const [dateRange, setDateRange] = useState();
  let t2;
  if ($[1] !== dateRange) {
    t2 = dateRangeToTimestamps(dateRange);
    $[1] = dateRange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    timestampGte,
    timestampLte
  } = t2;
  const {
    account
  } = useWeb3Context();
  let t3;
  if ($[3] !== transactionTypeFilter) {
    t3 = transactionTypeFilter.map(_temp).filter(isDefined);
    $[3] = transactionTypeFilter;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const activeTransactionTypes = t3;
  const t4 = account.address ?? null;
  let t5;
  if ($[5] !== t4) {
    t5 = {
      kind: "viewer",
      userAddress: t4
    };
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== activeTransactionTypes || $[8] !== address || $[9] !== chainId || $[10] !== t5 || $[11] !== timestampGte || $[12] !== timestampLte) {
    t6 = {
      address,
      chainId,
      vaultVersion: "v2",
      limit: PAGE_SIZE,
      filteredTransactionTypes: activeTransactionTypes,
      typeMap: VAULT_V2_TO_VAULT_TRANSACTION_TYPE,
      scope: t5,
      timestampGte,
      timestampLte
    };
    $[7] = activeTransactionTypes;
    $[8] = address;
    $[9] = chainId;
    $[10] = t5;
    $[11] = timestampGte;
    $[12] = timestampLte;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const {
    page,
    isLoading,
    isLoadingNextPage,
    error,
    pageIndex,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    prefetchNext
  } = useVaultTransactionsTableData(t6);
  const isMediumScreen = useIsBelowBreakpoint("m");
  let t7;
  if ($[14] !== chainId || $[15] !== page) {
    t7 = toVaultTransactionsTableRows({
      page,
      chainId,
      typeLabels: VAULT_V2_TRANSACTION_TYPE_LABELS
    });
    $[14] = chainId;
    $[15] = page;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const formattedTransactions = t7;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = selectedTransactionTypes => {
      startTransition(() => {
        setTransactionTypeFilter(selectedTransactionTypes);
      });
    };
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleFilterChange = t8;
  let t9;
  if ($[18] !== formattedTransactions) {
    t9 = {
      features: vaultTransactionsTableFeatures,
      data: formattedTransactions,
      columns: columnsDefs,
      enableSorting: false
    };
    $[18] = formattedTransactions;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const transactionsTable = useTable(t9);
  const loading = isLoading || isLoadingNextPage;
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      first: PAGE_SIZE,
      totalCount: PAGE_SIZE,
      pagination: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      columnsDefs
    };
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const loadingRows = useSkeletonRows(t10);
  let t11;
  if ($[21] !== dateRange) {
    t11 = <DateRangeFilter value={dateRange} onChange={setDateRange} side="bottom" align="end" page="vault_your_transactions" />;
    $[21] = dateRange;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== isMediumScreen || $[24] !== transactionTypeFilter) {
    t12 = isMediumScreen ? <div className="gap-xs flex flex-row items-center"><span className="text-3xs text-foreground">Type:</span><TransactionTypeFilter selectedTypes={transactionTypeFilter} onFilterChange={handleFilterChange} options={TRANSACTION_FILTER_OPTIONS} side="bottom" align="end" page="vault_your_transactions" /></div> : <TransactionTypeFilter selectedTypes={transactionTypeFilter} onFilterChange={handleFilterChange} options={TRANSACTION_FILTER_OPTIONS} side="bottom" align="end" page="vault_your_transactions" />;
    $[23] = isMediumScreen;
    $[24] = transactionTypeFilter;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t11 || $[27] !== t12) {
    t13 = <div className="gap-sm max-lg:gap-xs flex flex-wrap items-center">{t11}{t12}</div>;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const t14 = page?.partial ?? false;
  const t15 = transactionTypeFilter.length > 0 || Boolean(dateRange);
  let t16;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      handleFilterChange([]);
      setDateRange(undefined);
    };
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== error || $[31] !== loading || $[32] !== loadingRows || $[33] !== t14 || $[34] !== t15 || $[35] !== transactionsTable) {
    t17 = <TableScrollArea className="grid h-full w-full rounded-[20px]"><VaultV2TransactionsTableContent table={transactionsTable} loading={loading} error={error} partial={t14} loadingRows={loadingRows} hasActiveFilters={t15} clearFilters={t16} /></TableScrollArea>;
    $[30] = error;
    $[31] = loading;
    $[32] = loadingRows;
    $[33] = t14;
    $[34] = t15;
    $[35] = transactionsTable;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== canNextPage || $[38] !== canPreviousPage || $[39] !== error || $[40] !== isLoadingNextPage || $[41] !== nextPage || $[42] !== pageIndex || $[43] !== prefetchNext || $[44] !== previousPage) {
    t18 = <CursorPagination loading={isLoadingNextPage} error={error} pageIndex={pageIndex} previousPage={previousPage} nextPage={nextPage} canPreviousPage={canPreviousPage} canNextPage={canNextPage} onPrefetchNext={prefetchNext} />;
    $[37] = canNextPage;
    $[38] = canPreviousPage;
    $[39] = error;
    $[40] = isLoadingNextPage;
    $[41] = nextPage;
    $[42] = pageIndex;
    $[43] = prefetchNext;
    $[44] = previousPage;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== t17 || $[47] !== t18) {
    t19 = <div className="gap-lg flex h-[100%] w-[100%] flex-col"><div className="gap-xs flex flex-col">{t17}{t18}</div></div>;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t13 || $[50] !== t19 || $[51] !== title) {
    t20 = <VaultTransactionsLayout title={title} tableSettingsSlot={t13}>{t19}</VaultTransactionsLayout>;
    $[49] = t13;
    $[50] = t19;
    $[51] = title;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  return t20;
};
export default VaultV2YourTransactionsClient;
function _temp(transactionType) {
  return VaultV2TransactionType[transactionType as keyof typeof VaultV2TransactionType];
}